import React, { createContext, useContext, useEffect, useState } from 'react';
import { Theme, ThemeContextValue, ThemeProviderProps } from '../types/theme';

/**
 * Theme Context
 * Provides theme state and toggle functionality to all child components
 */
const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

/**
 * Custom hook to use theme context
 * @throws Error if used outside of ThemeProvider
 */
export const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

/**
 * ThemeProvider Component
 * Manages theme state, persistence, and provides theme context to children
 */
export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  defaultTheme = 'dark',
  storageKey = 'theme',
}) => {
  // Initialize theme from localStorage or use default
  const [theme, setThemeState] = useState<Theme>(() => {
    try {
      const storedTheme = localStorage.getItem(storageKey);
      return (storedTheme === 'light' || storedTheme === 'dark') 
        ? storedTheme 
        : defaultTheme;
    } catch (error) {
      console.warn('Failed to read theme from localStorage:', error);
      return defaultTheme;
    }
  });

  /**
   * Set theme and persist to localStorage
   */
  const setTheme = (newTheme: Theme): void => {
    setThemeState(newTheme);
    try {
      localStorage.setItem(storageKey, newTheme);
    } catch (error) {
      console.warn('Failed to save theme to localStorage:', error);
    }
  };

  /**
   * Toggle between light and dark themes
   */
  const toggleTheme = (): void => {
    setTheme(theme === 'light' ? 'dark' : 'light');
  };

  /**
   * Apply theme to document root element
   * This sets the data-theme attribute which CSS uses for theme switching
   */
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-theme', theme);
    
    // Diagnostic logging
    console.log('🎨 Theme changed to:', theme);
    console.log('📍 data-theme attribute:', root.getAttribute('data-theme'));
    console.log('🎨 Current CSS variables:');
    const styles = getComputedStyle(root);
    console.log('  --bg-main:', styles.getPropertyValue('--bg-main').trim());
    console.log('  --text-primary:', styles.getPropertyValue('--text-primary').trim());
    console.log('  --bg-surface:', styles.getPropertyValue('--bg-surface').trim());
    
    // Optional: Also add theme class for additional styling flexibility
    root.classList.remove('light', 'dark');
    root.classList.add(theme);
  }, [theme]);

  const contextValue: ThemeContextValue = {
    theme,
    setTheme,
    toggleTheme,
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;